<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.holder"
                name="bank_account.holder"
                rules="required"
                :label="$t('bank_account.holder')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.iban"
                name="bank_account.iban"
                rules="required|iban"
                :error-text="duplicateIbanError"
                :label="$t('bank_account.iban')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="formValues.bic"
                name="bank_account.bic"
                rules="bic"
                :label="$t('bank_account.bic')" />
        </div>
        <div class="flex items-end justify-end gap-2 sm:col-span-1">
            <BaseButtonMuted v-if="bankAccount" :disabled="saving" @click="$emit('cancel')">
                {{ $t('cancel') }}
            </BaseButtonMuted>
            <BaseButtonSecondary :disabled="saving" :loading="saving" @click="save()">
                {{ bankAccount ? $t('save') : $t('bank_account.add_bank_account') }}
            </BaseButtonSecondary>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useForm } from 'vee-validate'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseButtonSecondary from '../../../base/BaseButtonSecondary.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import {
    FrontendBankAccountResource,
    postBankAccountForLegalProfile,
    postBankAccountForNaturalProfile,
    putBankAccountUpdate,
} from '../../../../utilities/api'
import { notifySuccess, notifyError } from '../../../../utilities/useNotifications'
import { i18n } from '../../../../localization'

const props = defineProps<{
    profileUid: string
    profileType: 'natural' | 'legal'
    existingBankAccounts: FrontendBankAccountResource[]
    bankAccount?: FrontendBankAccountResource
}>()

const emit = defineEmits(['saved', 'cancel'])

const { validate } = useForm()

const formValues = reactive({
    holder: props.bankAccount?.holder ?? '',
    iban: props.bankAccount?.iban ?? '',
    bic: props.bankAccount?.bic ?? '',
})

const saving = ref(false)

const duplicateIbanError = computed(() => {
    if (!formValues.iban) {
        return undefined
    }
    const normalized = formValues.iban.replace(/\s+/g, '').toUpperCase()
    const isDuplicate = props.existingBankAccounts.some(
        (existing) =>
            existing.id !== props.bankAccount?.id && existing.iban.replace(/\s+/g, '').toUpperCase() === normalized
    )
    return isDuplicate ? i18n.global.t('validation_error.duplicate_iban') : undefined
})

async function save() {
    const { valid } = await validate()
    if (duplicateIbanError.value) {
        notifyError(duplicateIbanError.value)
        return
    }
    if (!valid) {
        return
    }

    saving.value = true
    try {
        if (props.bankAccount) {
            await putBankAccountUpdate(props.bankAccount.id, {
                holder: formValues.holder,
                iban: formValues.iban,
                bic: formValues.bic || null,
            })
        } else if (props.profileType === 'natural') {
            await postBankAccountForNaturalProfile(props.profileUid, {
                holder: formValues.holder,
                iban: formValues.iban,
                bic: formValues.bic || null,
            })
        } else {
            await postBankAccountForLegalProfile(props.profileUid, {
                holder: formValues.holder,
                iban: formValues.iban,
                bic: formValues.bic || null,
            })
        }
        notifySuccess(
            i18n.global.t(
                props.bankAccount ? 'bank_account.update_success_message' : 'bank_account.create_success_message'
            )
        )
        emit('saved')
    } catch (error) {
        // The api client's response interceptor already shows a notification with the actual
        // validation/business-rule message for 422/400/403 errors - nothing to add here.
    } finally {
        saving.value = false
    }
}
</script>
